import { ERROR_CODE } from '@/constant/errorCode'
import type { CommonError } from '@/dto/common/CommonError'

const NOTIFY_DEDUPE_MS = 5_000

export const isCaptchaError = (error: CommonError) =>
  error.code === ERROR_CODE.CAPTCHA_REQUIRED || error.code === ERROR_CODE.CAPTCHA_BLOCKED

type Deps = {
  /** 캡차 모달을 띄우고, 원 요청을 재시도해도 되면 true 로 끝난다 */
  requestPass: () => Promise<boolean>
  /** 지금은 이용할 수 없다는 공용 안내 */
  notifyLimited: () => void
  now?: () => number
}

/**
 * 서버가 추천·생성 API 에 429 로 캡차를 요구하거나 차단을 알릴 때의 처리.
 *
 * - 동시에 여러 요청이 캡차를 요구받아도 모달은 하나만 띄우고, 통과하면 기다리던 요청을 모두 재시도한다.
 * - 차단은 교사 단위라 동시에 여러 요청이 받는다. 안내는 5초에 한 번만 한다.
 * - 모달을 띄우지 못하면(배포 직후 청크 로드 실패 등) 공용 안내 후 원래 에러로 실패시킨다.
 * - 재시도한 요청이 또 캡차를 요구하면 모달을 다시 띄우지 않는다. 서버가 캡차 불필요(409)와 캡차 요구를
 *   번갈아 답하면 사람 개입 없이 모달이 열렸다 닫히기를 반복하기 때문이다.
 */
export const createCaptchaGate = ({ requestPass, notifyLimited, now = Date.now }: Deps) => {
  let pendingPass: Promise<boolean> | null = null
  let lastNotifiedAt = -Infinity

  const notifyOnce = () => {
    if (now() - lastNotifiedAt < NOTIFY_DEDUPE_MS) return
    lastNotifiedAt = now()
    notifyLimited()
  }

  const waitForPass = () => {
    pendingPass ??= requestPass().finally(() => {
      pendingPass = null
    })
    return pendingPass
  }

  const handle = async <T>(
    error: CommonError,
    { retried, retry }: { retried: boolean; retry: () => Promise<T> },
  ): Promise<T> => {
    if (error.code === ERROR_CODE.CAPTCHA_BLOCKED) {
      notifyOnce()
      return Promise.reject(error)
    }

    if (error.code !== ERROR_CODE.CAPTCHA_REQUIRED) return Promise.reject(error)

    if (retried) {
      notifyOnce()
      return Promise.reject(error)
    }

    let passed: boolean
    try {
      passed = await waitForPass()
    } catch {
      notifyOnce()
      return Promise.reject(error)
    }
    if (!passed) return Promise.reject(error)
    return retry()
  }

  return { handle }
}
